import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { MdKeyboardArrowDown, MdRefresh } from 'react-icons/md'
import styles from './styles.module.scss'
import { createNotification } from '../notifications/notifications'
import {
  getCertYears,
  getCertTerms,
  getCertFaculties,
  getCertExamTypes,
  getCertGroups,
  getCertDisciplines,
  getCertGradeValues,
  getCertGrid,
  setCertFilters,
  setCertData,
} from '../../actions/certification'

const ALL = '0'

// Выбор значения из списка:
// 1) если ранее выбранное значение есть в новом списке — оставить его;
// 2) иначе, если нужно, — текущее (is_current);
// 3) иначе — первый элемент.
const pickId = (list, key, preferred, useCurrent) => {
  if (!list || !list.length) return null
  if (preferred != null && list.some(item => String(item[key]) === String(preferred))) {
    return list.find(item => String(item[key]) === String(preferred))[key]
  }
  if (useCurrent) {
    const current = list.find(item => item.is_current)
    if (current) return current[key]
  }
  return list[0][key]
}

const Select = ({ className, value, options, onChange, disabled, placeholder }) => {
  const hasValue = options.some(o => String(o.value) === String(value))
  return (
    <div className={`${styles.selectWrap} ${className}`}>
      <select
        className={styles.select}
        value={hasValue ? String(value) : ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="" disabled hidden>{placeholder || '—'}</option>
        {options.map(o => (
          <option key={o.value} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
      <MdKeyboardArrowDown size={24} className={styles.selectArrow} />
    </div>
  )
}

const Filters = ({ examKind }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const initialized = useRef(false)
  const [busy, setBusy] = useState(false)

  const {
    years, terms, faculties, examTypes, groups, disciplines,
  } = useSelector(state => state.certification.filters)
  const loading = useSelector(state => state.certification.loading)

  // Выбранные значения хранятся в searchParams.
  const params = new URLSearchParams(location.search)
  const typeId = params.get('type_id') || ALL
  const yearId = params.get('year_id')
  const termId = params.get('term_id')
  const facultyId = params.get('faculty_id') || ALL
  const gid = params.get('gid')
  const cid = params.get('cid')

  // «Все» (0) в запрос не передаём.
  const reqVal = value => (value && value !== ALL ? value : '')

  const writeParams = (next) => {
    const sp = new URLSearchParams()
    Object.entries(next).forEach(([key, val]) => {
      if (val != null && val !== '') sp.set(key, val)
    })
    history.replace({ search: sp.toString() })
  }

  // --- шаги каскада (с очисткой зависимых списков и уведомлениями) ---

  const loadTerms = async (yearValue, preferredTerm, useCurrent) => {
    const list = await dispatch(getCertTerms(yearValue))
    if (!list || !list.length) {
      dispatch(setCertFilters({ groups: [], disciplines: [] }))
      dispatch(setCertData(null))
      createNotification('warning', 'Аттестация', 'За выбранный учебный год нет учебных периодов')
      return null
    }
    return pickId(list, 'trmid', preferredTerm, useCurrent)
  }

  const loadGroups = async (yearValue, termValue, facultyValue, typeValue, preferredGroup) => {
    const list = await dispatch(getCertGroups({
      exam_kind: examKind,
      year_id: yearValue,
      trmid: termValue,
      faculty_id: reqVal(facultyValue),
      typeids: reqVal(typeValue),
    }))
    if (!list || !list.length) {
      dispatch(setCertFilters({ disciplines: [] }))
      dispatch(setCertData(null))
      createNotification('warning', 'Аттестация', 'По выбранным фильтрам нет доступных групп')
      return null
    }
    return pickId(list, 'gid', preferredGroup, false)
  }

  const loadDisciplines = async (termValue, groupValue, typeValue, preferredDiscipline) => {
    const list = await dispatch(getCertDisciplines({
      exam_kind: examKind,
      gid: groupValue,
      trmid: termValue,
      typeids: reqVal(typeValue),
    }))
    if (!list || !list.length) {
      dispatch(setCertData(null))
      createNotification('warning', 'Аттестация', 'По выбранной группе нет дисциплин')
      return null
    }
    return pickId(list, 'cid', preferredDiscipline, false)
  }

  // --- обработчики ---

  // Начальная загрузка: тип + факультеты + справочник оценок, затем каскад
  // год (текущий) → период (текущий) → группа (первая) → дисциплина (первая).
  const init = async () => {
    setBusy(true)
    try {
      dispatch(getCertGradeValues())
      const [yearList] = await Promise.all([
        dispatch(getCertYears()),
        dispatch(getCertExamTypes(examKind)),
        dispatch(getCertFaculties()),
      ])
      if (!yearList || !yearList.length) {
        dispatch(setCertFilters({ terms: [], groups: [], disciplines: [] }))
        dispatch(setCertData(null))
        createNotification('warning', 'Аттестация', 'Список учебных годов пуст')
        writeParams({})
        return
      }
      const year = pickId(yearList, 'xp_key', yearId, true)

      const term = await loadTerms(year, termId, true)
      if (term == null) { writeParams({ type_id: typeId, year_id: year, faculty_id: facultyId }); return }

      const group = await loadGroups(year, term, facultyId, typeId, gid)
      if (group == null) { writeParams({ type_id: typeId, year_id: year, term_id: term, faculty_id: facultyId }); return }

      const discipline = await loadDisciplines(term, group, typeId, cid)
      writeParams({
        type_id: typeId, year_id: year, term_id: term,
        faculty_id: facultyId, gid: group, cid: discipline,
      })
    } finally {
      setBusy(false)
    }
  }

  // Смена года: периоды (текущий/первый) → группы → дисциплины, очистка таблицы.
  const onYearChange = async (value) => {
    setBusy(true)
    try {
      dispatch(setCertData(null))
      const term = await loadTerms(value, null, true)
      if (term == null) { writeParams({ type_id: typeId, year_id: value, faculty_id: facultyId }); return }

      const group = await loadGroups(value, term, facultyId, typeId, gid)
      if (group == null) { writeParams({ type_id: typeId, year_id: value, term_id: term, faculty_id: facultyId }); return }

      const discipline = await loadDisciplines(term, group, typeId, cid)
      writeParams({
        type_id: typeId, year_id: value, term_id: term,
        faculty_id: facultyId, gid: group, cid: discipline,
      })
    } finally {
      setBusy(false)
    }
  }

  // Смена периода / факультета / типа: группы → дисциплины, очистка таблицы.
  const reloadFromGroups = async (nextTerm, nextFaculty, nextType) => {
    setBusy(true)
    try {
      dispatch(setCertData(null))
      const base = { type_id: nextType, year_id: yearId, term_id: nextTerm, faculty_id: nextFaculty }

      const group = await loadGroups(yearId, nextTerm, nextFaculty, nextType, gid)
      if (group == null) { writeParams(base); return }

      const discipline = await loadDisciplines(nextTerm, group, nextType, cid)
      writeParams({ ...base, gid: group, cid: discipline })
    } finally {
      setBusy(false)
    }
  }

  const onTermChange = value => reloadFromGroups(value, facultyId, typeId)
  const onFacultyChange = value => reloadFromGroups(termId, value, typeId)
  const onTypeChange = value => reloadFromGroups(termId, facultyId, value)

  // Смена группы: дисциплины, очистка таблицы.
  const onGroupChange = async (value) => {
    setBusy(true)
    try {
      dispatch(setCertData(null))
      const discipline = await loadDisciplines(termId, value, typeId, cid)
      writeParams({
        type_id: typeId, year_id: yearId, term_id: termId,
        faculty_id: facultyId, gid: value, cid: discipline,
      })
    } finally {
      setBusy(false)
    }
  }

  // Смена дисциплины: только очистка таблицы, загрузка — по кнопке «Обновить».
  const onDisciplineChange = (value) => {
    dispatch(setCertData(null))
    writeParams({
      type_id: typeId, year_id: yearId, term_id: termId,
      faculty_id: facultyId, gid, cid: value,
    })
  }

  const onUpdate = () => {
    if (!termId || !gid || !cid) {
      createNotification('info', 'Аттестация', 'Выберите период, группу и дисциплину')
      return
    }
    dispatch(getCertGrid({
      exam_kind: examKind,
      gid,
      trmid: termId,
      cid,
      typeids: reqVal(typeId),
    }))
  }

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const typeOptions = [
    { value: ALL, label: 'Все' },
    ...(examTypes || []).map(t => ({ value: t.typeid, label: t.typename })),
  ]
  const yearOptions = (years || []).map(y => ({ value: y.xp_key, label: y.name }))
  const termOptions = (terms || []).map(t => ({ value: t.trmid, label: t.title }))
  const facultyOptions = [
    { value: ALL, label: 'Все' },
    ...(faculties || []).map(f => ({ value: f.idfaculty, label: f.faculty })),
  ]
  const groupOptions = (groups || []).map(g => ({ value: g.gid, label: g.name }))
  const disciplineOptions = (disciplines || []).map(d => ({ value: d.cid, label: d.title }))

  const canUpdate = Boolean(termId && gid && cid) && !busy && !loading

  return (
    <div className={styles.filters}>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Тип</span>
        <Select
          className={styles.cbType}
          value={typeId}
          options={typeOptions}
          onChange={onTypeChange}
          disabled={busy}
        />
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Учебный год</span>
        <Select
          className={styles.cbYear}
          value={yearId}
          options={yearOptions}
          onChange={onYearChange}
          disabled={busy || !yearOptions.length}
          placeholder="Нет данных"
        />
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Учебный период</span>
        <Select
          className={styles.cbTerm}
          value={termId}
          options={termOptions}
          onChange={onTermChange}
          disabled={busy || !termOptions.length}
          placeholder="Нет периодов"
        />
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Факультет</span>
        <Select
          className={styles.cbFaculty}
          value={facultyId}
          options={facultyOptions}
          onChange={onFacultyChange}
          disabled={busy}
        />
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Группа</span>
        <Select
          className={styles.cbGroup}
          value={gid}
          options={groupOptions}
          onChange={onGroupChange}
          disabled={busy || !groupOptions.length}
          placeholder="Нет групп"
        />
      </div>
      <div className={`${styles.field} ${styles.fieldGrow}`}>
        <span className={styles.fieldLabel}>Дисциплина</span>
        <Select
          className={styles.cbDiscipline}
          value={cid}
          options={disciplineOptions}
          onChange={onDisciplineChange}
          disabled={busy || !disciplineOptions.length}
          placeholder="Нет дисциплин"
        />
      </div>
      <button
        type="button"
        className={styles.updateButton}
        onClick={onUpdate}
        disabled={!canUpdate}
      >
        <MdRefresh size={20} />
        Обновить
      </button>
    </div>
  )
}

export default Filters
